import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Progress } from '@/components/ui/progress';
import { ConfirmDeleteModal } from '@/components/ConfirmDeleteModal';
import { FixButton } from '@/components/FixButton';
import {
  ChevronDown, ChevronUp, Plus, Trash2,
  DollarSign, Target, CalendarIcon, Check, X, Pencil, BookOpen, Clock
} from 'lucide-react';
import { format, isToday, isYesterday, differenceInDays, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { parseDateKey } from '@/types';

function formatDayLabel(dateKey) {
  const date = parseDateKey(dateKey);
  if (isToday(date)) return 'Hoy';
  if (isYesterday(date)) return 'Ayer';
  return format(date, "EEEE", { locale: es });
}

function getMonthAbbr(dateKey) {
  const date = parseDateKey(dateKey);
  return format(date, "MMM", { locale: es }).toUpperCase();
}

function getDayNum(dateKey) {
  return parseInt(dateKey.split('-')[2]);
}

function formatDeadlineShort(deadline) {
  if (!deadline) return null;
  try {
    const d = parseDateKey(deadline);
    return format(d, "d MMM", { locale: es });
  } catch {
    return deadline;
  }
}

function getDeadlineColor(deadline) {
  if (!deadline) return null;
  try {
    const deadlineDate = parseDateKey(deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysLeft = differenceInDays(deadlineDate, today);
    if (daysLeft <= 0) {
      return { bg: 'hsl(0, 70%, 45%)', text: '#fff' };
    } else if (daysLeft >= 30) {
      return { bg: 'hsl(220, 70%, 50%)', text: '#fff' };
    } else {
      const ratio = 1 - (daysLeft / 30);
      let hue;
      if (ratio <= 0.5) { hue = 220 + (ratio * 2) * 60; }
      else { hue = 280 + ((ratio - 0.5) * 2) * 80; }
      if (hue >= 360) hue = hue - 360;
      return { bg: `hsl(${hue}, 70%, 45%)`, text: '#fff' };
    }
  } catch {
    return { bg: 'hsl(220, 70%, 50%)', text: '#fff' };
  }
}

const MAX_VISIBLE_ITEMS = 20;

// ==================== TAREAS COLUMN ====================
function TareasColumn({ todos, dateKey, onAddTodo, onToggleTodo, onDeleteTodo, onSetTodoDeadline }) {
  const [newTodo, setNewTodo] = useState('');
  const [editDeadlineId, setEditDeadlineId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const handleAdd = () => {
    if (newTodo.trim()) {
      const defaultDeadline = format(addDays(new Date(), 1), 'yyyy-MM-dd');
      onAddTodo(dateKey, newTodo.trim(), defaultDeadline);
      setNewTodo('');
    }
  };

  const needsCollapse = todos.length > MAX_VISIBLE_ITEMS;

  return (
    <div className="day-column tareas-column" data-testid={`tareas-col-${dateKey}`}>
      <div className="column-header">
        <span className="column-dot dot-tareas"></span>
        <span className="column-title">TAREAS</span>
        {todos.length > 0 && <span className="column-badge">{todos.length}</span>}
      </div>
      <div className={`column-content column-scrollable ${!expanded && needsCollapse ? 'column-collapsed' : ''}`}>
        {todos.map((todo) => {
          const dlColor = getDeadlineColor(todo.deadline);
          return (
            <div key={todo.id} className="todo-item group">
              <button
                className={`todo-checkbox ${todo.completed ? 'checked' : ''}`}
                onClick={() => onToggleTodo(dateKey, todo.id)}
                data-testid={`todo-check-${todo.id}`}
              >
                {todo.completed && <Check className="h-3 w-3" />}
              </button>
              <span className={`todo-text ${todo.completed ? 'completed' : ''}`}>
                {todo.text}
              </span>
              {todo.deadline && dlColor && (
                <span className="deadline-badge" style={{ backgroundColor: dlColor.bg, color: dlColor.text }}>
                  {formatDeadlineShort(todo.deadline)}
                </span>
              )}
              {todo.carried_from && (
                <span className="carried-badge" title={`Arrastrada de ${todo.carried_from}`}>
                  <Clock className="h-2.5 w-2.5" />
                </span>
              )}
              <Popover open={editDeadlineId === todo.id} onOpenChange={(o) => setEditDeadlineId(o ? todo.id : null)}>
                <PopoverTrigger asChild>
                  <button className="icon-btn-sm opacity-0 group-hover:opacity-100" title="Cambiar fecha" data-testid={`edit-deadline-todo-${todo.id}`}>
                    <CalendarIcon className="h-3 w-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={todo.deadline ? parseDateKey(todo.deadline) : undefined}
                    defaultMonth={todo.deadline ? parseDateKey(todo.deadline) : new Date()}
                    onSelect={(date) => { if (date) onSetTodoDeadline(dateKey, todo.id, format(date, 'yyyy-MM-dd')); setEditDeadlineId(null); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <button className="delete-btn opacity-0 group-hover:opacity-100" onClick={() => setDeleteTarget(todo)} data-testid={`delete-todo-${todo.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
      {needsCollapse && (
        <button className="toggle-expand-btn" onClick={() => setExpanded(v => !v)} data-testid={`toggle-tasks-expand-${dateKey}`}>
          {expanded ? 'Mostrar menos' : `Mostrar todas (${todos.length})`}
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      )}
      <div className="add-row">
        <input
          type="text"
          placeholder="Nueva tarea..."
          value={newTodo}
          onChange={(e) => setNewTodo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          className="add-input"
          data-testid={`todo-input-${dateKey}`}
        />
        <button className="icon-btn" onClick={handleAdd} title="Agregar tarea" data-testid={`add-todo-btn-${dateKey}`}>
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="¿Eliminar tarea?"
        onConfirm={() => { onDeleteTodo(dateKey, deleteTarget.id); setDeleteTarget(null); }}
      />
    </div>
  );
}

// ==================== INLINE GOAL FORM ====================
function InlineGoalPanel({ goals, dateKey, onAddGoal, onUpdateGoalProgress, onSetGoalDeadline, onUpdateGoalTitle, onDeleteGoal }) {
  const [newGoal, setNewGoal] = useState('');
  const [deadlinePopover, setDeadlinePopover] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = () => {
    if (newGoal.trim()) {
      const defaultDeadline = format(addDays(new Date(), 14), 'yyyy-MM-dd');
      onAddGoal(dateKey, newGoal.trim(), 100, '%', defaultDeadline);
      setNewGoal('');
    }
  };

  const activeGoals = goals.filter(goal => {
    const pct = goal.target > 0 ? Math.min((goal.progress / goal.target) * 100, 100) : 0;
    return pct < 100;
  });

  return (
    <div className="inline-panel" data-testid={`goals-inline-${dateKey}`}>
      <div className="inline-panel-header">
        <Target className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="inline-panel-title">Objetivos</span>
        {activeGoals.length > 0 && <span className="column-badge">{activeGoals.length}</span>}
      </div>
      {activeGoals.length > 0 && (
        <div className="inline-panel-list">
          {activeGoals.map((goal) => {
            const pct = goal.target > 0 ? Math.min((goal.progress / goal.target) * 100, 100) : 0;
            const dlColor = getDeadlineColor(goal.deadline);
            return (
              <div key={goal.id} className="goal-item group">
                <div className="goal-header">
                  <span className="goal-title">{goal.title}</span>
                  <div className="goal-actions opacity-0 group-hover:opacity-100">
                    <FixButton text={goal.title} onApply={(fixed) => onUpdateGoalTitle(dateKey, goal.id, fixed)} />
                    <Popover open={deadlinePopover === goal.id} onOpenChange={(o) => setDeadlinePopover(o ? goal.id : null)}>
                      <PopoverTrigger asChild>
                        <button className="icon-btn-sm" title="Cambiar fecha" data-testid={`edit-deadline-goal-${goal.id}`}>
                          <CalendarIcon className="h-3 w-3" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          mode="single"
                          selected={goal.deadline ? parseDateKey(goal.deadline) : undefined}
                          defaultMonth={goal.deadline ? parseDateKey(goal.deadline) : new Date()}
                          onSelect={(date) => { if (date) onSetGoalDeadline(dateKey, goal.id, format(date, 'yyyy-MM-dd')); setDeadlinePopover(null); }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <button className="icon-btn-sm text-destructive" onClick={() => setDeleteTarget(goal)} data-testid={`delete-goal-${goal.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                {goal.deadline && dlColor && (
                  <span className="goal-deadline" style={{ color: dlColor.bg }}>
                    <CalendarIcon className="h-2.5 w-2.5" /> {formatDeadlineShort(goal.deadline)}
                  </span>
                )}
                <div className="goal-progress-row">
                  <Progress value={100 - pct} className="h-1.5 flex-1" />
                  <span className="goal-pct">{pct.toFixed(0)}%</span>
                </div>
                <div className="goal-add-progress">
                  <span className="goal-add-label">+</span>
                  <input
                    type="number" placeholder="Añadir..." className="goal-progress-input" min="1"
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value && parseFloat(e.target.value) > 0) { onUpdateGoalProgress(dateKey, goal.id, parseFloat(e.target.value)); e.target.value = ''; } }}
                    data-testid={`goal-progress-${goal.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="add-row">
        <input
          type="text" placeholder="Nuevo objetivo..." value={newGoal}
          onChange={(e) => setNewGoal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          className="add-input" data-testid={`goal-input-${dateKey}`}
        />
        <button className="icon-btn" onClick={handleAdd} title="Agregar objetivo" data-testid={`add-goal-btn-${dateKey}`}>
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="¿Eliminar objetivo?"
        onConfirm={() => { onDeleteGoal(dateKey, deleteTarget.id); setDeleteTarget(null); }}
      />
    </div>
  );
}

// ==================== INLINE EXPENSE FORM ====================
function InlineExpensePanel({ expenses, dateKey, onAddExpense, onDeleteExpense, projects = [] }) {
  const [amount, setAmount] = useState('');
  const [concepto, setConcepto] = useState('');
  const [projectId, setProjectId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = () => {
    if (amount && parseFloat(amount) > 0) {
      onAddExpense(dateKey, parseFloat(amount), concepto.trim(), projectId || null);
      setAmount(''); setConcepto(''); setProjectId(''); setShowForm(false);
    }
  };

  const shortName = (title) => {
    const words = (title || '').trim().split(/\s+/).slice(0, 2).join(' ');
    return words.length > 18 ? words.slice(0, 18) + '...' : words;
  };

  const total = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="inline-panel" data-testid={`expenses-inline-${dateKey}`}>
      <div className="inline-panel-header">
        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="inline-panel-title">Gastos</span>
        {total > 0 && <span className="column-badge">${total.toFixed(0)}</span>}
      </div>
      {expenses.length > 0 && (
        <div className="inline-panel-list">
          {expenses.map((expense) => (
            <div key={expense.id} className="expense-item group">
              <DollarSign className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="expense-desc">{expense.concepto || expense.description || 'Gasto'}</span>
              <span className="expense-amount">${expense.amount?.toFixed(2)}</span>
              <button className="delete-btn opacity-0 group-hover:opacity-100" onClick={() => setDeleteTarget(expense)} data-testid={`delete-expense-${expense.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {showForm ? (
        <div className="gastos-form">
          <input type="text" placeholder="Concepto..." value={concepto} onChange={(e) => setConcepto(e.target.value)} className="add-input" autoFocus data-testid={`expense-concepto-${dateKey}`} />
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="add-input text-xs" data-testid={`expense-project-${dateKey}`}>
            <option value="">Gastos en general</option>
            {projects.map(p => (<option key={p.id} value={p.id}>{shortName(p.title)}</option>))}
          </select>
          <div className="flex items-center gap-1">
            <span className="add-input-prefix">$</span>
            <input type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }} className="add-input add-input-amount" min="0" step="0.01" data-testid={`expense-amount-${dateKey}`} />
            <button className="icon-btn" onClick={handleAdd} data-testid={`add-expense-btn-${dateKey}`}><Plus className="h-3.5 w-3.5" /></button>
          </div>
          <button className="cancel-link" onClick={() => setShowForm(false)}>Cancelar</button>
        </div>
      ) : (
        <button className="add-entry-btn" onClick={() => setShowForm(true)} data-testid={`show-expense-form-${dateKey}`}>
          <Plus className="h-3 w-3" /> Agregar gasto
        </button>
      )}
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="¿Eliminar gasto?"
        onConfirm={() => { onDeleteExpense(dateKey, deleteTarget.id); setDeleteTarget(null); }}
      />
    </div>
  );
}

// ==================== BITÁCORA COLUMN ====================
function BitacoraColumn({ notes, dateKey, onAddNote, onUpdateNote, onDeleteNote, isCompact = false }) {
  const [newEntry, setNewEntry] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const handleAdd = () => {
    if (newEntry.trim()) { onAddNote(dateKey, newEntry.trim()); setNewEntry(''); }
  };

  const startEdit = (note) => { setEditingId(note.id); setEditText(note.text); };

  const saveEdit = () => {
    if (editText.trim()) { onUpdateNote(dateKey, editingId, editText.trim()); }
    setEditingId(null); setEditText('');
  };

  if (isCompact) {
    if (notes.length === 0) return null;
    return (
      <div className="bitacora-compact" data-testid={`bitacora-compact-${dateKey}`}>
        {notes.map((note) => (
          <div key={note.id} className="bitacora-compact-entry">
            <p className="bitacora-compact-text">{note.text}</p>
            <span className="bitacora-compact-time">{note.created_at ? format(new Date(note.created_at), "HH:mm") : ''}</span>
          </div>
        ))}
      </div>
    );
  }

  const needsCollapse = notes.length > MAX_VISIBLE_ITEMS;

  return (
    <div className="day-column bitacora-column" data-testid={`bitacora-col-${dateKey}`}>
      <div className="column-header">
        <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="column-title">BITÁCORA</span>
        {notes.length > 0 && <span className="column-badge">{notes.length}</span>}
      </div>
      <div className={`column-content bitacora-content column-scrollable ${!expanded && needsCollapse ? 'column-collapsed' : ''}`}>
        {notes.map((note) => (
          <div key={note.id} className="bitacora-entry group">
            {editingId === note.id ? (
              <div className="bitacora-edit">
                <textarea
                  value={editText} onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) saveEdit(); if (e.key === 'Escape') { setEditingId(null); setEditText(''); } }}
                  className="bitacora-textarea" autoFocus rows={3}
                />
                <div className="bitacora-edit-actions">
                  <button className="icon-btn" onClick={saveEdit}><Check className="h-3 w-3" /></button>
                  <button className="icon-btn" onClick={() => { setEditingId(null); setEditText(''); }}><X className="h-3 w-3" /></button>
                </div>
              </div>
            ) : (
              <div className="bitacora-entry-content">
                <span className="bitacora-time">{note.created_at ? format(new Date(note.created_at), "HH:mm") : ''}</span>
                <p className="bitacora-text">{note.text}</p>
                <div className="bitacora-actions opacity-0 group-hover:opacity-100">
                  <FixButton text={note.text} onApply={(fixed) => onUpdateNote(dateKey, note.id, fixed)} voice="first_person" />
                  <button className="icon-btn-sm" onClick={() => startEdit(note)}><Pencil className="h-3 w-3" /></button>
                  <button className="icon-btn-sm text-destructive" onClick={() => setDeleteTarget(note)} data-testid={`delete-note-${note.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {needsCollapse && (
        <button className="toggle-expand-btn" onClick={() => setExpanded(v => !v)} data-testid={`toggle-notes-expand-${dateKey}`}>
          {expanded ? 'Mostrar menos' : `Mostrar todas (${notes.length})`}
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      )}
      <textarea
        placeholder="Nueva entrada... (Ctrl+Enter para añadir)"
        value={newEntry} onChange={(e) => setNewEntry(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleAdd(); }}
        className="bitacora-textarea new-entry" rows={3}
        data-testid={`bitacora-input-${dateKey}`}
      />
      <button className="add-entry-btn" onClick={handleAdd} data-testid={`add-bitacora-btn-${dateKey}`}>
        <Plus className="h-3.5 w-3.5" /> Añadir entrada
      </button>
      <ConfirmDeleteModal
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
        title="¿Eliminar entrada de bitácora?"
        onConfirm={() => { onDeleteNote(dateKey, deleteTarget.id); setDeleteTarget(null); }}
      />
    </div>
  );
}

// ==================== MAIN DAY CARD ====================
export function DayCard({
  day, defaultOpen = false,
  onAddTodo, onToggleTodo, onDeleteTodo, onSetTodoDeadline,
  onAddNote, onUpdateNote, onDeleteNote,
  onAddExpense, onDeleteExpense,
  onAddGoal, onUpdateGoalProgress, onSetGoalDeadline, onUpdateGoalTitle, onDeleteGoal,
  projects,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showGoals, setShowGoals] = useState(false);
  const [showExpenses, setShowExpenses] = useState(false);

  const dateLabel = formatDayLabel(day.date);
  const dayNum = getDayNum(day.date);
  const monthAbbr = getMonthAbbr(day.date);
  const todosCount = day.todos?.length || 0;
  const completedCount = day.todos?.filter(t => t.completed).length || 0;
  const notesCount = day.notes?.length || 0;
  const goalsCount = day.goals?.length || 0;
  const expensesCount = day.expenses?.length || 0;
  const hasInfo = todosCount > 0 || goalsCount > 0 || expensesCount > 0 || notesCount > 0;
  const progressPct = todosCount > 0 ? (completedCount / todosCount) * 100 : 0;
  const expenseTotal = (day.expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="day-card" data-testid={`day-card-${day.date}`}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className="day-card-header">
            <div className="date-badge-container">
              {hasInfo && <span className="day-has-info-dot" aria-label="Día con información" role="status" />}
              <div className="date-badge">
                <span className="date-badge-num">{dayNum}</span>
                <span className="date-badge-month">{monthAbbr}</span>
              </div>
            </div>
            <div className="day-header-info">
              <span className="day-label capitalize">{dateLabel}</span>
              {!isOpen && (
                <div className="day-header-summary">
                  <div className="mini-progress">
                    <div className="mini-progress-bar" style={{ width: `${100 - progressPct}%` }}></div>
                  </div>
                  <span className="summary-stat"><Target className="h-3 w-3" /> {completedCount}/{todosCount}</span>
                  {notesCount > 0 && <span className="summary-stat"><BookOpen className="h-3 w-3" /> {notesCount}</span>}
                </div>
              )}
            </div>
            <div className="day-header-right">
              {isOpen && (
                <div className="day-progress-area">
                  <div className="day-progress-bar">
                    <div className="day-progress-fill" style={{ width: `${100 - progressPct}%` }}></div>
                  </div>
                  <div className="day-progress-stats">
                    <Target className="h-3 w-3" /> {completedCount}/{todosCount}
                    {notesCount > 0 && <> <BookOpen className="h-3 w-3 ml-2" /></>}
                  </div>
                </div>
              )}
              {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
            </div>
          </button>
        </CollapsibleTrigger>

        {!isOpen && day.notes?.length > 0 && (
          <div className="day-card-bitacora-always">
            <BitacoraColumn notes={day.notes || []} dateKey={day.date} onAddNote={onAddNote} onUpdateNote={onUpdateNote} onDeleteNote={onDeleteNote} isCompact={true} />
          </div>
        )}

        <CollapsibleContent>
          <div className="day-card-body">
            {/* Quick action buttons for Goals & Expenses */}
            <div className="quick-actions-bar" data-testid={`quick-actions-${day.date}`}>
              <button
                className={`quick-action-btn ${showGoals ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowGoals(v => !v); }}
                data-testid={`toggle-goals-inline-${day.date}`}
              >
                <Target className="h-3 w-3" />
                <span>Objetivos</span>
                {goalsCount > 0 && <span className="quick-action-count">{goalsCount}</span>}
              </button>
              <button
                className={`quick-action-btn ${showExpenses ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowExpenses(v => !v); }}
                data-testid={`toggle-expenses-inline-${day.date}`}
              >
                <DollarSign className="h-3 w-3" />
                <span>Gastos</span>
                {expenseTotal > 0 && <span className="quick-action-count">${expenseTotal.toFixed(0)}</span>}
              </button>
            </div>

            {/* Inline panels for Goals and Expenses */}
            {showGoals && (
              <InlineGoalPanel
                goals={day.goals || []} dateKey={day.date}
                onAddGoal={onAddGoal} onUpdateGoalProgress={onUpdateGoalProgress}
                onSetGoalDeadline={onSetGoalDeadline} onUpdateGoalTitle={onUpdateGoalTitle} onDeleteGoal={onDeleteGoal}
              />
            )}
            {showExpenses && (
              <InlineExpensePanel
                expenses={day.expenses || []} dateKey={day.date}
                onAddExpense={onAddExpense} onDeleteExpense={onDeleteExpense} projects={projects || []}
              />
            )}

            {/* Main 2-column layout: Tasks | Logbook */}
            <div className="day-columns">
              <TareasColumn
                todos={day.todos || []} dateKey={day.date}
                onAddTodo={onAddTodo} onToggleTodo={onToggleTodo}
                onDeleteTodo={onDeleteTodo} onSetTodoDeadline={onSetTodoDeadline}
              />
              <BitacoraColumn
                notes={day.notes || []} dateKey={day.date}
                onAddNote={onAddNote} onUpdateNote={onUpdateNote} onDeleteNote={onDeleteNote}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
